import React, { useState, useCallback, useRef } from 'react';
import {
  Button,
  Text,
  HelpTip,
  InputFormGroup,
  TextButton,
} from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import {
  COMBINE_ACCOUNTS_REQUEST_ACCESS_BUTTON_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_ERROR_SAME_TYPE_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_LINK_KEY,
  AMAZON_ADS_AGREEMENT_PATH,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_INTRO_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_ACCOUNT_ID_LABEL_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_PLACEHOLDER_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_ERROR_INVALID_ID_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_SA_HEADER_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_SA_BODY_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_DSP_HEADER_KEY,
  COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_DSP_BODY_KEY,
  COMBINE_ACCOUNTS_ERROR_MAX_ACCOUNTS_KEY,
  PENDING_ACCOUNT_NAME_PLACEHOLDER,
} from '@features/combine-accounts/constants';
import { AccountItem, getAccountTypeFromId } from '../types';
import {
  FlexOne,
  RequestAccessWrapper,
  RequestAgreement,
  RequestIntro,
  HelpTipContent,
} from '../style';

interface RequestAccessTabProps {
  selectedItems: AccountItem[];
  onAddItem: (item: AccountItem) => void;
  errorAlert: { message: string; targetId: string } | null;
  onError: (error: { message: string; targetId: string }) => void;
  onClearError: () => void;
}

const RequestAccessTab: React.FC<RequestAccessTabProps> = ({
  selectedItems,
  onAddItem,
  errorAlert,
  onError,
  onClearError,
}) => {
  const { intl } = useIntl();
  const [input, setInput] = useState('');
  const errorMessage =
    errorAlert?.targetId === 'request-access-input' ? errorAlert.message : null;

  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  const handleAdd = useCallback(() => {
    if (!input.trim()) return;
    const id = input.trim();
    const type = getAccountTypeFromId(id);
    if (!type) {
      onError({
        message: intl.getMessage(
          COMBINE_ACCOUNTS_REQUEST_ACCESS_ERROR_INVALID_ID_KEY,
        ),
        targetId: 'request-access-input',
      });
      return;
    }
    const prev = selectedItemsRef.current;
    if (prev.length >= 2) {
      onError({
        message: intl.getMessage(COMBINE_ACCOUNTS_ERROR_MAX_ACCOUNTS_KEY),
        targetId: 'request-access-input',
      });
      return;
    }
    if (prev.some((i) => i.accountType === type)) {
      onError({
        message: intl.getMessage(
          COMBINE_ACCOUNTS_REQUEST_ACCESS_ERROR_SAME_TYPE_KEY,
        ),
        targetId: 'request-access-input',
      });
      return;
    }
    onAddItem({
      id,
      // A requested account is one the user has no admin access to, so no name
      // is available. Show a placeholder dash as the name; the entered id is
      // rendered on the secondary line and the account type is conveyed by the
      // selected-list group header.
      label: PENDING_ACCOUNT_NAME_PLACEHOLDER,
      description: id,
      accountType: type,
      eligibilityStatus: { isEligible: true },
      isPendingAccess: true,
    });
    setInput('');
  }, [input, onAddItem, onError, intl]);

  return (
    <RequestAccessWrapper>
      <RequestIntro>
        <Text>
          {intl.getMessage(COMBINE_ACCOUNTS_REQUEST_ACCESS_INTRO_KEY)}
        </Text>
      </RequestIntro>
      <FlexOne>
        <InputFormGroup
          // Without fullWidth, Storm caps the form group at
          // theme.textarea.maxWidth.default, leaving the field and its
          // validation line visibly narrower than the intro copy above and the
          // agreement copy below. The design has all three flush.
          fullWidth
          id="request-access-input"
          type="text"
          label={
            <>
              {intl.getMessage(
                COMBINE_ACCOUNTS_REQUEST_ACCESS_ACCOUNT_ID_LABEL_KEY,
              )}
              <HelpTip position="top">
                <HelpTipContent>
                  <Text>
                    <strong>
                      {intl.getMessage(
                        COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_SA_HEADER_KEY,
                      )}
                    </strong>
                    <br />
                    {intl.getMessage(
                      COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_SA_BODY_KEY,
                    )}
                  </Text>
                  <Text>
                    <strong>
                      {intl.getMessage(
                        COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_DSP_HEADER_KEY,
                      )}
                    </strong>
                    <br />
                    {intl.getMessage(
                      COMBINE_ACCOUNTS_REQUEST_ACCESS_HELP_DSP_BODY_KEY,
                    )}
                  </Text>
                </HelpTipContent>
              </HelpTip>
            </>
          }
          placeholder={intl.getMessage(
            COMBINE_ACCOUNTS_REQUEST_ACCESS_PLACEHOLDER_KEY,
          )}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (errorMessage) onClearError();
          }}
          statusType={errorMessage ? 'error' : undefined}
          message={errorMessage ?? undefined}
        />
      </FlexOne>
      <RequestAgreement>
        <Text>
          {intl.formatMessage(COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_KEY, {
            agreement: (
              <TextButton
                onClick={() =>
                  window.open(
                    AMAZON_ADS_AGREEMENT_PATH,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                {intl.getMessage(
                  COMBINE_ACCOUNTS_REQUEST_ACCESS_AGREEMENT_LINK_KEY,
                )}
              </TextButton>
            ),
          })}
        </Text>
      </RequestAgreement>
      <Button
        id="request-access-request-button"
        taktId="request-access-request-button"
        disabled={!input.trim()}
        onClick={handleAdd}
      >
        {intl.getMessage(COMBINE_ACCOUNTS_REQUEST_ACCESS_BUTTON_KEY)}
      </Button>
    </RequestAccessWrapper>
  );
};

export default RequestAccessTab;
